import React, { useState, useMemo, useEffect } from 'react';
import { Box, Tabs, Tab, Card, CardContent, Typography, CircularProgress, Alert, TextField, Grid } from '@mui/material';
import { DataGrid, GridColDef, GridFilterModel, GridToolbar } from '@mui/x-data-grid';
import axios from 'axios';
import { ordVkApi } from '../services/api';
import { formatAppDateTime } from '../utils/dateFormat';

const mockCreatives = [
  { id: 1, name: 'Новости 1307', erid: '2VtzqumdGHu', type: 'Видео', link: 'Казаков Игорь Евгеньевич' },
  { id: 2, name: 'консоли 0907', erid: '2VtzquWzScf', type: 'Видео', link: 'Казаков Игорь Евгеньевич' },
];
const mockPlatforms = [
  { id: 1, name: 'Мой ТГ', type: 'Сайт', url: 'https://t.me/trendy_eng' },
  { id: 2, name: 'trendy_eng | Nintendo Switch, Xbox, PlayStation', type: 'Сайт', url: 'https://vk.com/trendy_eng' },
];
const mockStats = [
  { id: 1, creative: 'мелочи 2506', erid: '2VtzqvwbG9L', platform: 'trendy_eng | Nintendo Switch, Xbox, PlayStation', period: '24.06.2025 — 30.06.2025' },
  { id: 2, creative: 'Новости 2906', erid: '2VtzqwPHf4i', platform: 'trendy_eng | Nintendo Switch, Xbox, PlayStation', period: '28.06.2025 — 30.06.2025' },
];

const counterpartiesColumns: GridColDef[] = [
  { field: 'name', headerName: 'Название (ФИО)', flex: 1 },
  { field: 'inn', headerName: 'ИНН', flex: 1 },
  { field: 'externalId', headerName: 'ID', flex: 1 },
  { field: 'createDate', headerName: 'Дата создания', flex: 1, renderCell: (params) => params.value ? formatAppDateTime(params.value, true, '', true) : '' },
];

const creativesColumns: GridColDef[] = [
  { field: 'name', headerName: 'Название', flex: 1 },
  { field: 'erid', headerName: 'ERID', flex: 1 },
  { field: 'brand', headerName: 'Бренд', flex: 1 },
  { field: 'createDate', headerName: 'Дата создания', flex: 1, renderCell: (params) => params.value ? formatAppDateTime(params.value, true, '', true) : '' },
  { field: 'externalId', headerName: 'Внешний id', flex: 1 },
  { field: 'personExternalId', headerName: 'Id контрагента', flex: 1 },
];

const statisticsColumns: GridColDef[] = [
  { field: 'creativeExternalId', headerName: 'ID креатива', flex: 1 },
  { field: 'creativeName', headerName: 'Имя креатива', flex: 1 },
  { field: 'creativeErid', headerName: 'erid', flex: 1 },
  { field: 'showsCount', headerName: 'Просмотры', flex: 1 },
  { field: 'padExternalId', headerName: 'ID площадки', flex: 1 },
  { field: 'padName', headerName: 'Имя площадки', flex: 1 },
  { field: 'dateStartActual', headerName: 'Дата начала', flex: 1 },
  { field: 'dateEndActual', headerName: 'Дата окончания', flex: 1 },
];

const tabDefs = [
  { label: 'Контрагенты', columns: counterpartiesColumns },
  { label: 'Креативы', columns: creativesColumns, rows: mockCreatives },
  { label: 'Площадки', columns: [
    { field: 'name', headerName: 'Название', flex: 1 },
    { field: 'type', headerName: 'Тип', flex: 1 },
    { field: 'url', headerName: 'Ссылка', flex: 1 },
  ], rows: mockPlatforms },
  { label: 'Статистика', columns: statisticsColumns },
];

const OrdVkPage: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [counterparties, setCounterparties] = useState<any[]>([]);
  const [creatives, setCreatives] = useState<any[]>([]);
  const [statistics, setStatistics] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });

  // Фильтры для всех вкладок
  const [filters, setFilters] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    setFilterModel({ items: [] }); // сбрасываем фильтры только при смене вкладки
    if (tab === 0) {
      setLoading(true);
      setError(null);
      ordVkApi.getCounterparties()
        .then(data => {
          setCounterparties(data.map((item: any, idx: number) => ({ ...item, id: item.externalId || idx })));
        })
        .catch(e => {
          setError('Ошибка загрузки контрагентов');
        })
        .finally(() => setLoading(false));
    } else if (tab === 1) {
      setLoading(true);
      setError(null);
      ordVkApi.getCreatives()
        .then(data => {
          setCreatives(data.map((item: any, idx: number) => ({ ...item, id: item.erid || idx })));
        })
        .catch(e => {
          setError('Ошибка загрузки креативов');
        })
        .finally(() => setLoading(false));
    } else if (tab === 3) {
      setLoading(true);
      setError(null);
      ordVkApi.getStatistics()
        .then(data => {
          setStatistics(data.map((item: any, idx: number) => ({ ...item, id: item.externalId || idx })));
        })
        .catch(e => {
          setError('Ошибка загрузки статистики');
        })
        .finally(() => setLoading(false));
    }
  }, [tab]);

  // Получаем текущие колонки для фильтрации
  const currentColumns: GridColDef[] = useMemo(() => tabDefs[tab].columns, [tab]);

  // Фильтрация данных по всем полям
  const filteredRows = useMemo(() => {
    let baseRows: any[];
    if (tab === 0) baseRows = counterparties;
    else if (tab === 1) baseRows = creatives;
    else if (tab === 3) baseRows = statistics;
    else baseRows = (Array.isArray(tabDefs[tab].rows) ? tabDefs[tab].rows : []) || [];
    return baseRows.filter((row: any) =>
      currentColumns.every(col => {
        const filterValue = filters[col.field];
        if (!filterValue) return true;
        const cellValue = row[col.field];
        return cellValue !== undefined && cellValue !== null && String(cellValue).toLowerCase().includes(filterValue.toLowerCase());
      })
    );
  }, [tab, counterparties, creatives, statistics, filters, currentColumns]);

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 1.5 }}>ОРД VK</Typography>
      <Card>
        <CardContent>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
            {tabDefs.map((t, i) => <Tab key={t.label} label={t.label} />)}
          </Tabs>
          {/* Строка фильтров */}
          <Box display="flex" gap={1} mb={2}>
            {currentColumns.map(col => (
              <Box key={col.field} flex={1}>
                <TextField
                  size="small"
                  fullWidth
                  label={col.headerName}
                  value={filters[col.field] || ''}
                  onChange={e => setFilters(f => ({ ...f, [col.field]: e.target.value }))}
                  variant="outlined"
                />
              </Box>
            ))}
          </Box>
          {tab === 0 && loading && <CircularProgress sx={{ m: 2 }} />}
          {tab === 0 && error && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}
          {tab === 1 && loading && <CircularProgress sx={{ m: 2 }} />}
          {tab === 1 && error && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}
          <DataGrid
            rows={filteredRows}
            columns={currentColumns}
            pageSizeOptions={[10, 25, 50]}
            initialState={{
              pagination: { paginationModel: { page: 0, pageSize: 10 } },
            }}
            autoHeight
            disableRowSelectionOnClick
            disableColumnMenu={false}
            sortingOrder={['asc', 'desc']}
          />
        </CardContent>
      </Card>
    </Box>
  );
};

export default OrdVkPage; 